import * as React from "react";
import { useEffect, useRef } from "react";
import { documentService } from "../../services/documentService";
import type { NoteRefArt } from "../../services/vorgangService";
import { reinigeBlockHtml } from "./blockHtml";
import { gibBilderFrei, hydratisiereBilder } from "./bildDarstellung";
import type { Kommentar } from "./kommentarModell";
import { KLASSE_ANHAENGE } from "./kommentarInhalt";
import { dateiTypInfo } from "../documents/dateiTyp";
import "./kommentare.css";

/**
 * Der Inhalt EINES Kommentars — Klartext wie bisher, oder HTML mit
 * `#`-Chips und Anhängen (PO 28.09.2026).
 *
 * Gereinigt wird mit demselben Reiniger wie am Notizfeld: ein Kommentar ist
 * eine Notizzeile, und der Server reinigt Notiz-HTML nicht (`blockHtml.ts`).
 * Notizbilder kommen wie im Feld authentifiziert nach (`hydratisiereBilder`).
 *
 * Ein Klick auf einen Chip öffnet, worauf er zeigt. Ein DOKUMENT öffnet auch
 * dann, wenn niemand einen Weg dafür hereinreicht — ein Anhang, der sich
 * nicht öffnen lässt, wäre keiner.
 */
export const KommentarText: React.FC<{
    k: Pick<Kommentar, "html" | "istHtml">;
    className: string;
    onBezugOeffnen?: (art: NoteRefArt, id: string) => void;
}> = ({ k, className, onBezugOeffnen }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const html = k.istHtml ? reinigeBlockHtml(k.html) : null;

    useEffect(() => {
        const el = ref.current;
        if (!el || !html || !/data-bild-id/.test(html)) return;
        const urls: string[] = [];
        void hydratisiereBilder(el, { merke: (u) => urls.push(u) });
        return () => gibBilderFrei(urls);
    }, [html]);

    // Angehängte Dokumente zeigen ihren Dateityp (User 22.10.2026). Der Chip
    // kennt nur den Dateinamen — das genügt für die Einordnung; Kürzel und
    // Farbe malt das CSS (`kommentare.css`) aus den Attributen.
    useEffect(() => {
        const el = ref.current;
        if (el && html && html.includes(KLASSE_ANHAENGE)) markiereAnhangTypen(el);
    }, [html]);

    if (html == null) return <div className={className}>{k.html}</div>;

    return (
        <div
            ref={ref}
            className={`${className} nb-ktext`}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: html }}
            onClick={(e) => {
                const chip = (e.target as HTMLElement | null)?.closest?.("[data-ref-art][data-ref-id]");
                if (!chip) return;
                const art = chip.getAttribute("data-ref-art") as NoteRefArt | null;
                const id = chip.getAttribute("data-ref-id");
                if (!art || !id) return;
                e.preventDefault();
                e.stopPropagation();
                if (onBezugOeffnen) onBezugOeffnen(art, id);
                else if (art === "dokument") void documentService.openSignedDownload(Number(id));
            }}
        />
    );
};

/**
 * Setzt an jedem Dokument-Chip der Anhangzeile Kürzel, Farbe und Typnamen.
 * Ohne erkennbare Endung bleibt der Chip, wie er war (Büroklammer).
 */
export function markiereAnhangTypen(wurzel: HTMLElement): void {
    for (const chip of Array.from(
        wurzel.querySelectorAll<HTMLElement>(`.${KLASSE_ANHAENGE} [data-ref-art="dokument"]`),
    )) {
        const { kuerzel, label, farbe } = dateiTypInfo((chip.textContent ?? "").trim());
        if (!kuerzel) continue;
        chip.setAttribute("data-dt-kuerzel", kuerzel);
        chip.style.setProperty("--dt-farbe", farbe);
        if (!chip.title) chip.title = `${label} (${kuerzel})`;
    }
}
